import type { ProtocolData, Farm, lockedStats } from "@/services";

function calculateAverageRate(farms: Farm[]) {
    const sum = farms.reduce((acc, farm) => acc + farm.exitApy * farm.assetsNormalized, 0);
    const totalWeight = farms.reduce((acc, farm) => acc + farm.assetsNormalized, 0);
    return sum / totalWeight;
}

function calculateFarmAverageRate(farms: Farm[]) {
    const liquidFarms = farms.filter((farm) => farm.maturityTimestampMs === 0);
    const illiquidFarms = farms.filter((farm) => farm.maturityTimestampMs !== 0);

    const avgLiquidRate = calculateAverageRate(liquidFarms);
    const avgIlliquidRate = calculateAverageRate(illiquidFarms);

    return {
        avgLiquidRate,
        avgIlliquidRate,
    };
}

function calculateBucketAllocation(totalIlliquid: number, buckets: Record<string, lockedStats>): number[] {
    const bucketArray = Object.values(buckets);

    // Sort buckets by bucketMaturity
    bucketArray.sort((a, b) => a.bucketMaturity - b.bucketMaturity);

    // Return only the allocation values as a number array
    return bucketArray.map((bucket) => bucket.totalLockedNormalized / totalIlliquid);
}

function evaluateInfinifiModel(
    liquidDepositSize: number,
    illiquidDepositSize: number,
    liquidReturnMultiplier: number,
    illiquidReturnMultiplier: number,
    currentDrag: number,
    reserveFactor: number,
    targetIlliquid: number,
    liquidReturnRate: number,
    illiquidReturnRate: number,
    totalLiquidUSDC: number,
    totalIlliquidUSDC: number,
    totalUnwindingNormalized: number,
    unwindingMultiplier: number,
    smootherRate: number,
) {
    if (reserveFactor > 100 || reserveFactor < 0) {
        throw new Error("Reserve factor cannot be greater than 100 or less than 0");
    }

    if (targetIlliquid > 100 || targetIlliquid < 0) {
        throw new Error("Target illiquid cannot be greater than 100 or less than 0");
    }

    if (liquidReturnRate < 0) {
        throw new Error("Liquid return rate cannot be less than 0");
    }

    if (illiquidReturnRate < 0) {
        throw new Error("Illiquid return rate cannot be less than 0");
    }

    const totalStakedDepositSize = Math.max(0, liquidDepositSize - currentDrag);

    // Calculate amounts: locked not unwinding vs unwinding
    const amountLockedNotUnwinding = illiquidDepositSize - totalUnwindingNormalized;
    const amountUnwinding = totalUnwindingNormalized;

    // Calculate illiquid product: amount locked NOT UNWINDING * max(illiquidReturnMultiplier, targetIlliquid) + amount unwinding * unwindingMultiplier
    const maxIlliquidMultiplier = Math.max(illiquidReturnMultiplier, targetIlliquid);
    const illiquidProduct = amountLockedNotUnwinding * maxIlliquidMultiplier + amountUnwinding * unwindingMultiplier;

    const products = {
        liquid: totalStakedDepositSize * liquidReturnMultiplier,
        illiquid: illiquidProduct,
    };

    const liquidYieldNormal = totalLiquidUSDC * liquidReturnRate;
    const illiquidYieldNormal = totalIlliquidUSDC * illiquidReturnRate;

    // Add J-Curve (smoother rate) to total yield: USDC per second -> annual
    const annualSmootherUSDC = smootherRate * 31_536_000; // 365 * 24 * 60 * 60
    const totalReturn = liquidYieldNormal + illiquidYieldNormal + annualSmootherUSDC;
    const totalProduct = products.liquid + products.illiquid;

    const liquidYieldInfiniFi = (products.liquid / totalProduct) * totalReturn;
    const illiquidYieldInfiniFi = totalReturn - liquidYieldInfiniFi;

    const liquidAPR = liquidYieldInfiniFi / totalStakedDepositSize;
    const illiquidAPR = illiquidYieldInfiniFi / illiquidDepositSize;

    return {
        expectedReturn: totalReturn,
        boostedRate: liquidAPR + illiquidAPR,
        liquidYieldInfiniFi,
        illiquidYieldInfiniFi,
        liquidAPR,
        illiquidAPR,
    };
}

function calculateAPY(
    deposit: number,
    selectedSide: "liquid" | "illiquid",
    protocolData: ProtocolData,
    bucket?: number,
) {
    const { avgLiquidRate, avgIlliquidRate } = calculateFarmAverageRate(protocolData.farms);

    const { totalSupplyNormalized, totalLockedNormalized, totalStakedNormalized, totalUnwindingNormalized } =
        protocolData.stats.receipt;

    const liquidDepositSize = totalSupplyNormalized - totalLockedNormalized;
    const illiquidDepositSize = totalLockedNormalized;
    const currentDrag = liquidDepositSize - totalStakedNormalized;

    const totalLiquidUSDC = protocolData.stats.asset.totalLiquidAssetNormalized;
    const totalIlliquidUSDC = protocolData.stats.asset.totalIlliquidAssetNormalized;

    const {
        reserveRatio: reserveFactor,
        liquidReturnMultiplier,
        illiquidReturnMultiplier,
        illiquidTargetRatio: targetIlliquid,
        unwindingMultiplier,
        smootherRate,
    } = protocolData.params;

    const bucketAllocation = calculateBucketAllocation(illiquidDepositSize, protocolData.stats.locked);
    const bucketMultiplier = protocolData.params.bucketMultipliers;

    // Liquid Side calculation
    if (selectedSide === "liquid") {
        const spreadsheetModel = evaluateInfinifiModel(
            liquidDepositSize + deposit,
            illiquidDepositSize,
            liquidReturnMultiplier,
            illiquidReturnMultiplier,
            currentDrag,
            reserveFactor,
            targetIlliquid,
            avgLiquidRate,
            avgIlliquidRate,
            totalLiquidUSDC,
            totalIlliquidUSDC,
            totalUnwindingNormalized,
            unwindingMultiplier,
            smootherRate,
        );

        // Smoother rate is already included in the total yield and distributed proportionally
        return spreadsheetModel.liquidAPR * 0.9;
    }

    // Illiquid Side calculation
    const newIlliquidDepositSize = illiquidDepositSize + deposit;

    const spreadsheetModel = evaluateInfinifiModel(
        liquidDepositSize,
        newIlliquidDepositSize,
        liquidReturnMultiplier,
        illiquidReturnMultiplier,
        currentDrag,
        reserveFactor,
        targetIlliquid,
        avgLiquidRate,
        avgIlliquidRate,
        totalLiquidUSDC,
        totalIlliquidUSDC,
        totalUnwindingNormalized,
        unwindingMultiplier,
        smootherRate,
    );

    // We need the bucket parameter for illiquid side
    if (bucket === undefined || bucket < 1 || bucket > bucketAllocation.length) {
        throw new Error(`Invalid bucket: ${bucket}`);
    }

    // Calculate bucket allocation sizes
    const bucketAllocationSize: Record<number, number> = {};
    for (let i = 0; i < bucketAllocation.length; i++) {
        bucketAllocationSize[i + 1] = bucketAllocation[i] * newIlliquidDepositSize;
    }

    // Calculate bucket reward weights (allocation × multiplier)
    // Yield is distributed proportionally to reward weight (Level 2 from system)
    const bucketRewardWeights: Record<number, number> = {};
    for (let i = 0; i < bucketMultiplier.length; i++) {
        bucketRewardWeights[i + 1] = bucketMultiplier[i] * bucketAllocationSize[i + 1];
    }

    // Calculate unwinding reward weight (amount unwinding × unwinding multiplier)
    const unwindingRewardWeight = totalUnwindingNormalized * unwindingMultiplier;

    // Calculate total reward weight (buckets + unwinding)
    const totalBucketRewardWeight = Object.values(bucketRewardWeights).reduce((acc, curr) => acc + curr, 0);
    const totalRewardWeight = totalBucketRewardWeight + unwindingRewardWeight;

    // Calculate yield amount received per bucket
    // Yield is distributed proportionally to (allocation × multiplier) including unwinding
    const yieldAmountReceivedPerBucket: Record<number, number> = {};
    for (let i = 1; i <= bucketAllocation.length; i++) {
        yieldAmountReceivedPerBucket[i] =
            (bucketRewardWeights[i] / totalRewardWeight) * spreadsheetModel.illiquidYieldInfiniFi;
    }

    // Calculate APR map
    // APR = (yield / allocation) × 0.9
    // Multiplier is already baked into yield distribution, so we just divide by allocation
    const aprMap: Record<number, number> = {};
    for (let i = 1; i <= bucketAllocation.length; i++) {
        aprMap[i] = (yieldAmountReceivedPerBucket[i] / bucketAllocationSize[i]) * 0.9;
    }

    return aprMap[bucket];
}

export function calculateStakeAPY(depositAmount: number, protocolData: ProtocolData) {
    return calculateAPY(depositAmount, "liquid", filterOutPendleCUSDO(protocolData));
}

export function calculateLockAPY(depositAmount: number, unwindingPeriod: number, protocolData: ProtocolData) {
    return calculateAPY(depositAmount, "illiquid", filterOutPendleCUSDO(protocolData), unwindingPeriod);
}

function filterOutPendleCUSDO(protocolData: ProtocolData) {
    return {
        ...protocolData,
        farms: protocolData.farms.filter((farm) => farm.name !== "pendle-PT-cUSDO-19JUN2025"),
    };
}
